import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Icon } from '@iconify/react';
import logoImage from '../assets/images/logo.webp';

function escape(str: string) {
  return str.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Google "G" logo with official colors (blue, red, yellow, green) */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default function AuthModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [emailError, setEmailError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (email && !EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  }, [email]);

  useEffect(() => {
    if (fullName && fullName.trim().length < 2) {
      setNameError('Please enter your full name (at least 2 characters).');
    } else {
      setNameError('');
    }
  }, [fullName]);

  useEffect(() => {
    if (cooldown > 0) {
      const id = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(id);
    }
  }, [cooldown]);

  const handleMagicLink = async () => {
    if (emailError || nameError || cooldown > 0) return;
    setLoading(true);
    setMessage('');
    const cleanEmail = email.trim();
    const cleanName = fullName.trim();
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: { data: { full_name: cleanName, email: cleanEmail } },
      });
      if (error) {
        setMessage('Could not send magic link. Please try again.');
      } else {
        setShowSuccess(true);
        setEmail('');
        setFullName('');
        setCooldown(30);
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2000);
      }
    } catch {
      setMessage('Internal error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setMessage('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });
      if (error) setMessage('Could not sign in with Google. Please try again.');
    } catch {
      setMessage('Internal error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !loading && email && fullName && !emailError && !nameError && cooldown === 0;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-0 sm:p-4 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div
        className="relative w-full h-[100dvh] bg-[#fbf8f5] rounded-none shadow-2xl my-auto max-h-[100dvh] overflow-y-auto overscroll-contain sm:h-auto sm:max-w-[400px] sm:max-h-[calc(100vh-2rem)] sm:rounded-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-title"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full text-gray-900/80 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none"
        >
          <Icon icon="mdi:close" className="w-5 h-5" />
        </button>

        <div className="bg-[#fbf8f5] px-6 pt-8 pb-6 text-center border-b border-gray-200">
          <img src={logoImage} alt="Wincrest Logistics" className="h-20 mx-auto mb-4" />
          <h2 id="login-title" className="text-xl font-bold text-gray-900">Sign in</h2>
          <p className="text-sm text-gray-900/80 mt-1">Use your email or Google to continue</p>
        </div>

        <div className="p-6 sm:p-8 bg-[#fbf8f5]">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="auth-fullName" className="block text-left text-sm font-medium text-gray-900/80">Full name</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon icon="mdi:account-outline" className="w-5 h-5" />
                </span>
                <input
                  id="auth-fullName"
                  type="text"
                  placeholder="John Smith"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={loading}
                  autoFocus
                  className={`w-full pl-11 pr-4 py-3.5 rounded-xl border-2 bg-gray-50/50 text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 disabled:opacity-50 ${
                    nameError ? 'border-red-300 focus:border-red-400 focus:ring-red-200 bg-red-50/50' : 'border-gray-200'
                  }`}
                />
              </div>
              {nameError && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5"><Icon icon="mdi:alert-circle-outline" className="w-4 h-4" />{escape(nameError)}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="auth-email" className="block text-left text-sm font-medium text-gray-900/80">Email</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <Icon icon="mdi:email-outline" className="w-5 h-5" />
                </span>
                <input
                  id="auth-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className={`w-full pl-11 pr-4 py-3.5 rounded-xl border-2 bg-gray-50/50 text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 disabled:opacity-50 ${
                    emailError ? 'border-red-300 focus:border-red-400 focus:ring-red-200 bg-red-50/50' : 'border-gray-200'
                  }`}
                />
              </div>
              {emailError && <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1.5"><Icon icon="mdi:alert-circle-outline" className="w-4 h-4" />{escape(emailError)}</p>}
            </div>
          </div>

          <button
            type="button"
            onClick={handleMagicLink}
            disabled={!canSubmit}
            className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-primary text-white font-semibold shadow-lg shadow-primary/25 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <Icon icon="mdi:loading" className="w-5 h-5 animate-spin" />
                Sending link…
              </>
            ) : cooldown > 0 ? (
              `Try again in ${cooldown}s`
            ) : (
              <>
                <Icon icon="mdi:email-outline" className="w-5 h-5" />
                Send magic link
              </>
            )}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <span className="relative flex justify-center text-xs font-medium text-gray-900/80 bg-[#fbf8f5] px-3">or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border-2 border-gray-200 bg-white text-[#3c4043] font-semibold hover:bg-[#f8f9fa] hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4285F4]/30 focus:ring-offset-2 focus:ring-offset-[#fbf8f5] transition-all disabled:opacity-50 shadow-sm"
          >
            {loading ? (
              <Icon icon="mdi:loading" className="w-5 h-5 animate-spin text-[#4285F4]" />
            ) : (
              <>
                <GoogleIcon className="w-5 h-5 flex-shrink-0" />
                <span className="text-[#3c4043]">Continue with Google</span>
              </>
            )}
          </button>

          {message && (
            <div className="mt-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700">
              <Icon icon="mdi:alert-circle-outline" className="w-5 h-5 flex-shrink-0" />
              {escape(message)}
            </div>
          )}

          <p className="mt-6 text-center text-xs text-gray-900/80">Secure sign-in. We never share your data.</p>
        </div>

        {showSuccess && (
          <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-[130] flex items-center gap-3 px-5 py-4 bg-emerald-600 text-white rounded-xl shadow-xl">
            <Icon icon="mdi:check-circle" className="w-6 h-6 flex-shrink-0" />
            <span className="font-medium">Check your email for the sign-in link.</span>
          </div>
        )}
      </div>
    </div>
  );
}
